import { useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu } from 'lucide-react';
import type { MeResponse } from '@ucp/web/lib';
import { myWorkAppliesToMe } from '../../lib/myWork/applies';
import { ShellWorkLink } from '../my-work/ShellWorkLink';
import { showMyActions } from '../ops/opsRoles';
import { cn } from '../ui/cn';
import { Sheet } from '../ui/Sheet';
import { NavMenuIconLabel } from './NavMenuIconLabel';
import { iconForGroup, iconForModule, iconForPortalOps, iconForSubModule } from './navIcons';
import {
  portalOpsConfig,
  railModel,
  subModuleOwnsPath,
  type ModuleDef,
  type ModuleSubModule,
  type PortalOpsId,
} from './modulesConfig';

/**
 * Drawer row styling — soft active tint + icon/label (PRO-0540). Distinct from the
 * desktop `ModuleRail` border-l treatment; matches the reference sidebar pattern.
 */
function moduleDrawerLinkClasses(isActive: boolean) {
  return cn(
    'flex min-h-11 w-full items-center rounded-lg px-3 py-2.5 text-sm no-underline transition-colors ease-ds',
    isActive
      ? 'bg-info-bg font-semibold text-info'
      : 'font-normal text-text-secondary hover:bg-muted',
  );
}

function DrawerWork({ user, onClose }: { user: MeResponse; onClose: () => void }) {
  const show =
    user.featureFlags.myWorkEnabled === true ? myWorkAppliesToMe(user) : showMyActions(user);
  if (!show) return null;
  return (
    <>
      <ShellWorkLink
        user={user}
        variant="drawer"
        onNavigate={onClose}
        drawerLinkClassName={moduleDrawerLinkClasses}
      />
      <div aria-hidden className="my-2 border-t border-border" />
    </>
  );
}

/**
 * Mobile tier-1 module launcher — a header burger that opens a left-anchored glass
 * drawer of module rows (ADR-014 Amendment 7 / ADR-015 amendment 2026-07-09). Layout
 * follows the reference sidebar pattern: vertical icon + label list, soft active row
 * highlight — not a card grid.
 */
export interface ModuleLauncherTriggerProps {
  activeModule: ModuleDef;
  modules: readonly ModuleDef[];
  showPortalOps?: boolean;
  activePortalOpsId?: PortalOpsId | null;
  /**
   * PRO-0600 / ADR-014 A8. Off (default) → flat drawer byte-for-byte; on → the
   * scope-based grouped drawer (labelled non-interactive group headers + member
   * links), parity with the desktop rail (`railModel`).
   */
  navRestructureEnabled?: boolean;
  /**
   * PRO-0740 / ADR-014 amendment (MF-2). When true, no module/sub-module row is
   * marked active (the non-module `/account` route), mirroring the desktop rail.
   */
  suppressActiveModule?: boolean;
  /**
   * PRO-3100 / ADR-014 A16 - the caller. When set, the My actions row renders above the
   * module list under the same predicate as the header link.
   */
  user?: MeResponse;
}

export function ModuleLauncherTrigger({
  activeModule,
  modules,
  showPortalOps = false,
  activePortalOpsId = null,
  navRestructureEnabled = false,
  suppressActiveModule = false,
  user,
}: ModuleLauncherTriggerProps) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const { pathname } = useLocation();

  const renderModuleRow = (module: ModuleDef) => {
    const isActive = !activePortalOpsId && !suppressActiveModule && module.id === activeModule.id;
    return (
      <li key={module.id}>
        <Link
          to={module.basePath}
          aria-current={isActive ? 'page' : undefined}
          onClick={() => setOpen(false)}
          className={moduleDrawerLinkClasses(isActive)}
        >
          <NavMenuIconLabel icon={iconForModule(module.id)} active={isActive}>
            <span className="truncate">{module.label}</span>
          </NavMenuIconLabel>
        </Link>
      </li>
    );
  };

  // Sub-module row for a standalone-nested section (HR/Finance): the interactive
  // link uniform with a group member row, so the drawer mirrors the desktop rail.
  const renderSubModuleRow = (sub: ModuleSubModule, moduleId: ModuleDef['id']) => {
    const isActive =
      !activePortalOpsId && !suppressActiveModule && subModuleOwnsPath(sub, pathname, moduleId);
    return (
      <li key={sub.id}>
        <Link
          to={sub.basePath}
          aria-current={isActive ? 'page' : undefined}
          onClick={() => setOpen(false)}
          className={moduleDrawerLinkClasses(isActive)}
        >
          <NavMenuIconLabel icon={iconForSubModule(sub.id)} active={isActive}>
            <span className="truncate">{sub.label}</span>
          </NavMenuIconLabel>
        </Link>
      </li>
    );
  };

  const entries = railModel(modules, navRestructureEnabled);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        aria-label={`Open modules menu — ${activeModule.label}`}
        aria-expanded={open}
        aria-controls="module-launcher"
        aria-haspopup="dialog"
        onClick={() => setOpen(true)}
        className="inline-flex min-h-11 min-w-11 shrink-0 items-center justify-center rounded-md border border-border bg-workspace p-2 text-text-primary shadow-sm hover:bg-muted"
      >
        <Menu aria-hidden className="h-5 w-5 text-text-secondary" />
      </button>

      <Sheet
        open={open}
        onClose={() => setOpen(false)}
        returnFocusRef={triggerRef}
        anchor="left"
        tone="glass-sidebar"
        id="module-launcher"
        aria-labelledby="module-launcher-title"
        className="w-[72%] max-w-sm"
      >
        <div className="flex h-full w-full flex-col">
          <div className="px-4 pb-2 pt-4">
            <h2 id="module-launcher-title" className="m-0 text-lg font-semibold text-text-primary">
              Modules
            </h2>
          </div>
          <nav aria-label="Modules" className="flex flex-1 flex-col overflow-y-auto px-3 pb-4">
            {user ? <DrawerWork user={user} onClose={() => setOpen(false)} /> : null}
            <ul className="m-0 flex list-none flex-col gap-0.5 p-0" role="list">
              {entries.map((entry) => {
                if (entry.kind === 'module') {
                  return renderModuleRow(entry.module);
                }
                if (entry.kind === 'section') {
                  // Standalone nested module (HR/Finance) as a scope header: sub-module
                  // rows are the always-visible links (module dashboard is not a target),
                  // indented to mirror a group section on the desktop rail.
                  const labelId = `launcher-section-${entry.module.id}`;
                  const SectionIcon = iconForModule(entry.module.id);
                  return (
                    <li key={`section-${entry.module.id}`}>
                      <p
                        id={labelId}
                        className="mb-1 mt-2 flex items-center gap-2 px-3 text-xs font-semibold uppercase tracking-wide text-text-secondary"
                      >
                        <SectionIcon aria-hidden className="h-4 w-4 shrink-0" />
                        {entry.module.label}
                      </p>
                      <ul
                        aria-labelledby={labelId}
                        className="m-0 ml-3 flex list-none flex-col gap-0.5 p-0"
                        role="list"
                      >
                        {entry.subModules.map((sub) => renderSubModuleRow(sub, entry.module.id))}
                      </ul>
                    </li>
                  );
                }
                const GroupIcon = iconForGroup(entry.group.id);
                const labelId = `launcher-group-${entry.group.id}`;
                return (
                  <li key={`group-${entry.group.id}`}>
                    <p
                      id={labelId}
                      className="mb-1 mt-2 flex items-center gap-2 px-3 text-xs font-semibold uppercase tracking-wide text-text-secondary"
                    >
                      <GroupIcon aria-hidden className="h-4 w-4 shrink-0" />
                      {entry.group.label}
                    </p>
                    <ul
                      aria-labelledby={labelId}
                      className="m-0 ml-3 flex list-none flex-col gap-0.5 p-0"
                      role="list"
                    >
                      {entry.members.map((member) => renderModuleRow(member))}
                    </ul>
                  </li>
                );
              })}
            </ul>
            {showPortalOps ? (
              <>
                <div aria-hidden className="my-3 border-t border-border" />
                <ul className="m-0 flex list-none flex-col gap-0.5 p-0" role="list">
                  {portalOpsConfig.map((ops) => {
                    const isActive = activePortalOpsId === ops.id;
                    return (
                      <li key={ops.id}>
                        <Link
                          to={ops.defaultPath}
                          aria-current={isActive ? 'page' : undefined}
                          onClick={() => setOpen(false)}
                          className={moduleDrawerLinkClasses(isActive)}
                        >
                          <NavMenuIconLabel icon={iconForPortalOps(ops.id)} active={isActive}>
                            {ops.label}
                          </NavMenuIconLabel>
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </>
            ) : null}
          </nav>
        </div>
      </Sheet>
    </>
  );
}
